import { errorService } from './errorService';

const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// Global loading state for API requests
let activeRequests = 0;
const loadingCallbacks: Array<(isLoading: boolean) => void> = [];

export const subscribeToLoadingState = (
  callback: (isLoading: boolean) => void
) => {
  loadingCallbacks.push(callback);
  return () => {
    const index = loadingCallbacks.indexOf(callback);
    if (index > -1) {
      loadingCallbacks.splice(index, 1);
    }
  };
};

const setGlobalLoading = (isLoading: boolean) => {
  if (isLoading) {
    activeRequests++;
  } else {
    activeRequests = Math.max(0, activeRequests - 1);
  }

  const hasActiveRequests = activeRequests > 0;
  loadingCallbacks.forEach(callback => callback(hasActiveRequests));
};

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: Array<{ field: string; message: string }>;
}

// User interface for free app
interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  createdAt: string;
  lastLoginAt?: string;
  loginCount: number;
  joinDate: string;
}

class ApiClient {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Public method to expose the private request method for admin service
  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    showGlobalLoading: boolean = true
  ): Promise<ApiResponse<T>> {
    return this._request(endpoint, options, showGlobalLoading);
  }

  private async _request<T>(
    endpoint: string,
    options: RequestInit = {},
    showGlobalLoading: boolean = true
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (showGlobalLoading) {
      setGlobalLoading(true);
    }

    try {
      const response = await fetch(url, config);

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');

      let data;
      if (isJson) {
        data = await response.json();
      } else {
        data = { success: false, message: 'Invalid response format' };
      }

      if (!response.ok) {
        const errorMessage =
          data.message || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    } catch (error) {
      // Only log errors in development mode to reduce console noise
      if (import.meta.env.DEV) {
        console.warn('API request failed (backend not available):', endpoint);
      }

      let enhancedError: any = error;

      // Enhanced error handling with network detection
      if (error instanceof TypeError && error.message.includes('fetch')) {
        if (!navigator.onLine) {
          enhancedError = new Error(
            'You appear to be offline. Please check your internet connection.'
          );
          enhancedError.isNetworkError = true;
        } else {
          enhancedError = new Error(
            'Network error. Unable to connect to the server.'
          );
          enhancedError.isNetworkError = true;
        }
      }

      // Handle different HTTP status codes
      if (error instanceof Response) {
        enhancedError = errorService.handleApiError(error);
      }

      // Add context to error
      enhancedError.context = {
        endpoint,
        method: options.method || 'GET',
        timestamp: new Date().toISOString(),
      };

      throw enhancedError;
    } finally {
      if (showGlobalLoading) {
        setGlobalLoading(false);
      }
    }
  }



  // Test endpoints
  async getSampleQuestions(params?: {
    count?: number;
    category?: string;
    difficulty?: string;
  }): Promise<ApiResponse<{ questions: any[] }>> {
    const queryParams = new URLSearchParams();
    if (params?.count) queryParams.append('count', params.count.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.difficulty) queryParams.append('difficulty', params.difficulty);

    const query = queryParams.toString();
    return this._request(`/tests/sample-questions${query ? `?${query}` : ''}`);
  }

  async createTestSession(
    testType: 'standard' | 'comprehensive'
  ): Promise<ApiResponse<{ session: any }>> {
    return this._request('/tests/sessions', {
      method: 'POST',
      body: JSON.stringify({ testType: testType.toUpperCase() }),
    });
  }

  async getTestSession(
    sessionId: string
  ): Promise<ApiResponse<{ session: any }>> {
    return this._request(`/tests/sessions/${sessionId}`);
  }

  async getUserTestSessions(params?: {
    page?: number;
    limit?: number;
    status?: string;
    testType?: string;
  }): Promise<ApiResponse<{ sessions: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.testType) queryParams.append('testType', params.testType);

    const query = queryParams.toString();
    return this._request(`/tests/sessions${query ? `?${query}` : ''}`);
  }

  async submitTestAnswer(
    sessionId: string,
    answerData: {
      questionId: string;
      selectedAnswer: string | number;
      timeSpent: number;
    }
  ): Promise<ApiResponse<{ answer: any; progress: any }>> {
    return this._request(`/tests/sessions/${sessionId}/answers`, {
      method: 'POST',
      body: JSON.stringify(answerData),
    });
  }

  async completeTest(sessionId: string): Promise<ApiResponse<{ result: any }>> {
    return this._request(`/tests/sessions/${sessionId}/complete`, {
      method: 'POST',
    });
  }

  async abandonTest(sessionId: string): Promise<ApiResponse> {
    return this._request(`/tests/sessions/${sessionId}/abandon`, {
      method: 'POST',
    });
  }

  async getUserTestResults(params?: {
    page?: number;
    limit?: number;
    testType?: string;
  }): Promise<ApiResponse<{ results: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.testType) queryParams.append('testType', params.testType);

    const query = queryParams.toString();
    return this._request(`/tests/results${query ? `?${query}` : ''}`);
  }

  async getTestResult(resultId: string): Promise<ApiResponse<{ result: any }>> {
    return this._request(`/tests/results/${resultId}`);
  }

  async getDetailedTestResult(
    resultId: string
  ): Promise<ApiResponse<{ result: any }>> {
    return this._request(`/tests/results/${resultId}/detailed`);
  }

  async generatePDFReport(resultId: string): Promise<Blob> {
    const response = await fetch(
      `${this.baseURL}/tests/results/${resultId}/pdf`
    );

    if (!response.ok) {
      throw new Error('Failed to generate PDF report');
    }

    return response.blob();
  }
}

export const apiClient = new ApiClient();
export type { User, ApiResponse };
